/**
 * EL-195 — as REGRAS do balão do "i" de ajuda dos campos (lote de 09/10/2026, item 1).
 *
 * POR QUE ISTO EXISTE
 *   Pedido: "Eliminar todas as tooltips que estão sem conteúdo dentro." O que o usuário via
 *   era: "Clico no 'i' e nada abre". A dica morava no `title` nativo, que só aparece parando
 *   o mouse ~1 s em cima — no clique e no toque, nada. E em notebook (1280–1536 px) o "i" ainda
 *   saía CORTADO pelo rótulo: um "(" sem nada dentro. O "i" virou botão e a dica, um balão
 *   que abre no clique, no toque e no teclado.
 *
 * Este módulo é PURO (sem DOM) de propósito: os testes rodam em Node, e o que dá errado em
 * silêncio num balão é a CONTA — sair da tela pela borda, subir para baixo da barra superior
 * fixa, a seta apontar para o lugar errado — e a decisão do clique (o balão aberto pelo mouse
 * que FECHA quando a pessoa clica para fixá-lo). Quem desenha é `ui/balao-info.ts`.
 */

/** Distância entre o "i" e o balão (a seta mora nesse vão). */
export const AFASTAMENTO = 8;
/** Folga mínima até a borda da janela, a barra superior e a faixa do rodapé. */
export const MARGEM = 8;
/** A seta nunca encosta na quina arredondada do balão. */
export const SETA_MIN = 14;

/** Um retângulo na tela, em px, com a origem no canto superior esquerdo da janela. */
export interface Caixa {
  esquerda: number;
  topo: number;
  largura: number;
  altura: number;
}

/**
 * Onde o balão PODE ficar. `topoLivre` é a borda de baixo da barra superior fixa (o balão
 * nunca sobe para debaixo dela); `baseLivre`, o topo da faixa de ambiente do rodapé (ou a
 * altura da janela, sem faixa).
 */
export interface Limites {
  largura: number;
  topoLivre: number;
  baseLivre: number;
}

export interface Posicao {
  esquerda: number;
  topo: number;
  /** De que lado do "i" o balão ficou — decide para onde a seta aponta. */
  lado: 'cima' | 'baixo';
  /** Onde a seta fica, medido a partir da borda esquerda do balão. */
  setaX: number;
}

const limitar = (v: number, min: number, max: number): number => Math.min(Math.max(v, min), max);

/**
 * Posiciona o balão junto do "i".
 *
 * PREFERE EM CIMA: o "i" mora no rótulo, logo acima do campo — o balão embaixo cobriria
 * exatamente o campo que a pessoa está tentando entender. Sem espaço em cima (o "i" encostado
 * na barra superior), vai para baixo; se nenhum dos dois lados couber inteiro, fica no lado
 * com mais espaço e é empurrado para dentro. Na horizontal, centrado no "i" e empurrado para
 * dentro nas bordas; a seta continua apontando para o "i" mesmo quando o balão se desloca.
 */
export function posicionarBalao(
  ancora: Caixa,
  balao: { largura: number; altura: number },
  lim: Limites,
): Posicao {
  const centro = ancora.esquerda + ancora.largura / 2;
  const esquerda = limitar(centro - balao.largura / 2, MARGEM, Math.max(MARGEM, lim.largura - MARGEM - balao.largura));

  const topoAcima = ancora.topo - AFASTAMENTO - balao.altura;
  const topoAbaixo = ancora.topo + ancora.altura + AFASTAMENTO;
  const cabeAcima = topoAcima >= lim.topoLivre + MARGEM;
  const cabeAbaixo = topoAbaixo + balao.altura <= lim.baseLivre - MARGEM;
  let lado: Posicao['lado'];
  if (cabeAcima) lado = 'cima';
  else if (cabeAbaixo) lado = 'baixo';
  else lado = ancora.topo - lim.topoLivre >= lim.baseLivre - (ancora.topo + ancora.altura) ? 'cima' : 'baixo';

  const minTopo = lim.topoLivre + MARGEM;
  const maxTopo = Math.max(minTopo, lim.baseLivre - MARGEM - balao.altura);
  const topo = limitar(lado === 'cima' ? topoAcima : topoAbaixo, minTopo, maxTopo);

  const setaX = limitar(centro - esquerda, SETA_MIN, Math.max(SETA_MIN, balao.largura - SETA_MIN));
  return { esquerda, topo, lado, setaX };
}

/** Como o balão foi aberto. O do mouse fecha ao sair do "i"; o do clique fica até a pessoa fechar. */
export type ModoBalao = 'clique' | 'mouse';

/**
 * O que um clique no "i" faz (Enter e Espaço no teclado e o toque chegam como clique).
 *
 *   · nada aberto, ou aberto o de OUTRO "i"  → abrir este (um balão por vez);
 *   · o deste "i", aberto por parar o mouse → FIXAR: a pessoa clicou para ler com calma, e
 *     fechar ali seria o "clico e nada abre" de novo, só que pior (abre e some);
 *   · o deste "i", aberto por clique         → fechar.
 */
export function acaoDoClique(aberto: { mesmoI: boolean; modo: ModoBalao } | null): 'abrir' | 'fixar' | 'fechar' {
  if (aberto === null || !aberto.mesmoI) return 'abrir';
  return aberto.modo === 'mouse' ? 'fixar' : 'fechar';
}

/**
 * O texto que o balão mostra, ou `null` se não há o que mostrar. Um "i" sem texto não deve
 * nem nascer (`fldRotulo` no simulador já não o desenha); isto é a segunda trava, para o balão
 * nunca abrir vazio.
 */
export function textoDaDica(bruto: string | null | undefined): string | null {
  const t = (bruto ?? '').trim();
  return t === '' ? null : t;
}
